"use client";

import { useEffect } from "react";
import { type TutorDomainSurface, getCourseAttribution } from "./courseAttribution";
import { getExactDomainPostHogClient } from "./exactDomainPostHogClient";

export const FRENCH_COURSE_CTA_EVENT = "french_course_cta_clicked";
export const FRENCH_COURSE_CHECKOUT_EVENT = "french_course_checkout_started";
export const FRENCH_COURSE_PURCHASE_EVENT = "french_course_purchase_completed";

const FRENCH_COURSE_PROPERTIES = {
  course_language: "French",
  course_slug: "french_intensive_2026_12",
  course_start_date: "2026-12-07",
  course_end_date: "2026-12-18",
  course_price: 300,
  currency: "EUR",
} as const;

function captureFrenchCourseEvent(
  eventName: string,
  properties: Record<string, unknown>,
  surface: TutorDomainSurface
) {
  const client = getExactDomainPostHogClient();
  if (!client) return false;

  client.capture(eventName, {
    ...FRENCH_COURSE_PROPERTIES,
    ...getCourseAttribution("french", surface),
    ...properties,
  });
  return true;
}

export function FrenchCourseAnalytics({
  enabled,
  surface = "exact-domain",
}: {
  enabled: boolean;
  surface?: TutorDomainSurface;
}) {
  useEffect(() => {
    if (!enabled) return;

    function handleClick(event: MouseEvent) {
      const clickedElement = event.target instanceof Element ? event.target : null;
      const cta = clickedElement?.closest<HTMLElement>("[data-french-course-cta]");
      if (!cta) return;

      captureFrenchCourseEvent(
        FRENCH_COURSE_CTA_EVENT,
        {
          cta_location: cta.dataset.courseCtaLocation || "unknown",
          cta_target: cta.dataset.courseCtaTarget || "unknown",
          cta_text: cta.textContent?.trim() || "unknown",
        },
        surface
      );
    }

    function handleSubmit(event: SubmitEvent) {
      const form = event.target instanceof HTMLFormElement ? event.target : null;
      if (!form?.matches("[data-french-course-checkout]")) return;

      captureFrenchCourseEvent(
        FRENCH_COURSE_CHECKOUT_EVENT,
        {
          checkout_location: form.dataset.courseCheckoutLocation || "unknown",
          course_variant: form.dataset.courseVariant || "unknown",
        },
        surface
      );
    }

    document.addEventListener("click", handleClick, true);
    document.addEventListener("submit", handleSubmit, true);

    return () => {
      document.removeEventListener("click", handleClick, true);
      document.removeEventListener("submit", handleSubmit, true);
    };
  }, [enabled, surface]);

  return null;
}

type FrenchCoursePurchaseAnalyticsProps = {
  transactionId: string;
  courseVariant: string;
  productId: string;
  value: number;
  currency: string;
  surface?: TutorDomainSurface;
};

export function FrenchCoursePurchaseAnalytics({
  transactionId,
  courseVariant,
  productId,
  value,
  currency,
  surface = "exact-domain",
}: FrenchCoursePurchaseAnalyticsProps) {
  useEffect(() => {
    const storageKey = `posthog_french_course_purchase:${transactionId}`;

    try {
      if (window.localStorage.getItem(storageKey)) return;
    } catch {
      // Capture still works when browser storage is unavailable.
    }

    const captured = captureFrenchCourseEvent(
      FRENCH_COURSE_PURCHASE_EVENT,
      {
        transaction_id: transactionId,
        course_variant: courseVariant,
        product_id: productId,
        value,
        currency: currency.toUpperCase(),
        verification_source: "stripe_checkout_session",
      },
      surface
    );
    if (!captured) return;

    try {
      window.localStorage.setItem(storageKey, "sent");
    } catch {
      // The transaction_id property remains available for query-time deduplication.
    }
  }, [courseVariant, currency, productId, surface, transactionId, value]);

  return null;
}
